import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 20 — memory temporal validity. `useMemoryHistory` reads a memory's
 * supersession chain (`get_memory_history`); `useSupersedeMemory` performs the
 * owner-only fact correction (`supersede_memory`) and invalidates the affected
 * caches so the panel, history drawer, and semantic search all refresh.
 */

export interface MemoryRef {
  id: string;
  name: string;
  status: string | null;
  valid_from: string | null;
  valid_until: string | null;
}

export interface MemoryHistory {
  memory_id: string;
  current_validity: {
    valid_from: string | null;
    valid_until: string | null;
    superseded_by: string | null;
  };
  /** Older facts this memory replaced — nearest first. */
  ancestors: MemoryRef[];
  /** Newer facts that replaced this memory — nearest first. */
  descendants: MemoryRef[];
}

export function useMemoryHistory(memoryId: string | undefined, enabled = true) {
  return useQuery<MemoryHistory | null>({
    queryKey: ["memory-history", memoryId],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ history: MemoryHistory }>("get_memory_history", { memory_id: memoryId });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Failed to load history");
      return r.structuredContent?.history ?? null;
    },
    enabled: enabled && !!memoryId,
    staleTime: 10_000,
  });
}

export interface SupersedeMemoryArgs {
  memory_id: string;
  superseded_by_memory_id?: string;
  reason?: string;
  archive_old?: boolean;
}

export function useSupersedeMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: SupersedeMemoryArgs): Promise<void> => {
      const r = await mcpClient.toolsCall("supersede_memory", {
        memory_id: args.memory_id,
        ...(args.superseded_by_memory_id ? { superseded_by_memory_id: args.superseded_by_memory_id } : {}),
        ...(args.reason ? { reason: args.reason } : {}),
        ...(args.archive_old ? { archive_old: true } : {}),
      });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Supersede failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["child-nodes"] });
      qc.invalidateQueries({ queryKey: ["memory-history"] });
      qc.invalidateQueries({ queryKey: ["semantic-memory-search"] });
    },
  });
}
